import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import "./Settings.css";
import Slider from "../components/Slider";
import Modal from "../components/Modal";
import VideoModal from "../components/VideoModal";
import Tooltip from "../components/Tooltip";
import QRCodeModal from "../components/QRCodeModal";
import Menu from "../components/Menu";
import {
  isSuperadminModeEnabled,
  SUPERADMIN_MODE_EVENT,
  SUPERADMIN_MODE_STORAGE_KEY,
} from "../components/SuperadminHeader";
import { lotService, lotPricingService } from "../utils/api";
import { useLot } from "../contexts/LotContext";
import { useUser } from "../contexts/UserContext";
import { useDirtyState } from "../contexts/DirtyStateContext";
import Error from "../components/Error";
import { formatDisplayLotId, formatReadableDate } from "../utils/format";

const Settings: React.FC = () => {
  const { customerId, lotId } = useParams<{ customerId: string; lotId: string }>();
  const navigate = useNavigate();
  const { user, fetchUserLots } = useUser();

  // Get lot context methods
  const { lotData: contextLotData, invalidateData } = useLot();

  // State for lot data
  const [lotName, setLotName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [lotCapacity, setLotCapacity] = useState("0");
  // Added registryOn state to store the current registry status
  const [registryOn, setRegistryOn] = useState(false);
  // Added ownerCustomerId state from params or fetched lot data
  const [ownerCustomerId, setOwnerCustomerId] = useState(customerId || "");
  const [lotData, setLotData] = useState<any>(null);
  // Add state for lot pricing
  const [lotPricingData, setLotPricingData] = useState<any>(null);
  const [lotDisplayId, setLotDisplayId] = useState("");
  const [lotStatus, setLotStatus] = useState("Active");
  const [lotCreatedOn, setLotCreatedOn] = useState<string | null>(null);
  const [lotModifiedOn, setLotModifiedOn] = useState<string | null>(null);
  const [lotLatitude, setLotLatitude] = useState<number | null>(null);
  const [lotLongitude, setLotLongitude] = useState<number | null>(null);

  // Address modal state
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [streetNumber, setStreetNumber] = useState("");
  const [streetName, setStreetName] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("Canada");
  const [provinceState, setProvinceState] = useState("");
  const [postalCode, setPostalCode] = useState("");

  // Address validation state
  const [streetNumberError, setStreetNumberError] = useState(false);
  const [streetNameError, setStreetNameError] = useState(false);
  const [cityError, setCityError] = useState(false);
  const [provinceStateError, setProvinceStateError] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState(false);
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);
  const [isProvinceMenuOpen, setIsProvinceMenuOpen] = useState(false);

  // Add state for address last updated time and lock status
  const [addressLastUpdatedOn, setAddressLastUpdatedOn] = useState<string | null>(null);
  const [isAddressLocked, setIsAddressLocked] = useState(false);
  const [remainingTime, setRemainingTime] = useState(""); // New state for remaining time

  const parseCoordinate = (value: any): number | null => {
    if (value === undefined || value === null) {
      return null;
    }
    const numeric = typeof value === "number" ? value : parseFloat(value);
    return Number.isNaN(numeric) ? null : numeric;
  };

  // Canadian provinces
  const canadianProvinces = [
    { value: "AB", label: "Alberta" },
    { value: "BC", label: "British Columbia" },
    { value: "MB", label: "Manitoba" },
    { value: "NB", label: "New Brunswick" },
    { value: "NL", label: "Newfoundland and Labrador" },
    { value: "NS", label: "Nova Scotia" },
    { value: "NT", label: "Northwest Territories" },
    { value: "NU", label: "Nunavut" },
    { value: "ON", label: "Ontario" },
    { value: "PE", label: "Prince Edward Island" },
    { value: "QC", label: "Quebec" },
    { value: "SK", label: "Saskatchewan" },
    { value: "YT", label: "Yukon" }
  ];

  // US states
  const usStates = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "DC", label: "District of Columbia" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" }
  ];

  // Pricing settings
  const [hourlyPrice, setHourlyPrice] = useState("");
  const [dailyMaxPrice, setDailyMaxPrice] = useState("");
  const [gracePeriod, setGracePeriod] = useState("10"); // Default to 10 min
  const [maxTime, setMaxTime] = useState("");
  const [ticketAmount, setTicketAmount] = useState("");

  // Toggles
  const [freeParking, setFreeParking] = useState(false);
  const [allowValidation, setAllowValidation] = useState(false);
  const [allowBetaAi, setAllowBetaAi] = useState(false);
  const [developmentLot, setDevelopmentLot] = useState(false);
  const [isBetaUpdating, setIsBetaUpdating] = useState(false);
  const [isDevelopmentUpdating, setIsDevelopmentUpdating] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [isDeletingLot, setIsDeletingLot] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingAdvancedToggle, setPendingAdvancedToggle] = useState<{ field: "allowBetaAi" | "developmentLot"; value: boolean } | null>(null);

  // State for modals
  const [modalType, setModalType] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState("");
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  // Track if any changes were made
  const [isDirty, setIsDirty] = useState(false);
  const [saveButtonOpacity, setSaveButtonOpacity] = useState(0.3);

  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [isSuperadminModeOn, setIsSuperadminModeOn] = useState<boolean>(() => isSuperadminModeEnabled());
  const accountRole = (user?.role || "").toLowerCase();
  const isAccountSuperadmin = accountRole === "superadmin";
  const canUseSuperadminTools = isAccountSuperadmin && isSuperadminModeOn;
  const [hasCopiedLotId, setHasCopiedLotId] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);

  // Add field label mapping
  const fieldLabels: { [key: string]: string } = {
    lotName: "Lot Name",
    companyName: "Company Name",
    address: "Address",
    lotCapacity: "Lot Capacity"
  };

  // Character limits for lot settings fields
  const fieldLimits: { [key: string]: number } = {
    lotName: 35,
    companyName: 50,
    address: 100,
    lotCapacity: 5 // 5 digits maximum
  };

  // Validation functions for lot settings fields
  const validateLotName = (name: string): boolean => {
    return name.trim().length > 0 && name.trim().length <= fieldLimits.lotName;
  };

  const validateCompanyName = (name: string): boolean => {
    return name.trim().length > 0 && name.trim().length <= fieldLimits.companyName;
  };

  const validateAddress = (address: string): boolean => {
    return address.trim().length > 0 && address.trim().length <= fieldLimits.address;
  };

  const validateLotCapacity = (capacity: string): boolean => {
    const numValue = parseInt(capacity);
    return capacity.trim().length > 0 &&
      capacity.trim().length <= fieldLimits.lotCapacity &&
      !isNaN(numValue) &&
      numValue >= 0;
  };

  // State for error validation
  const [gracePeriodError, setGracePeriodError] = useState(false);
  const [maxTimeError, setMaxTimeError] = useState(false);
  const [maxTimeMinError, setMaxTimeMinError] = useState(false);
  const [hourlyPriceError, setHourlyPriceError] = useState(false);
  const [dailyMaxPriceError, setDailyMaxPriceError] = useState(false);
  const [dailyMaxPriceEmptyError, setDailyMaxPriceEmptyError] = useState(false);
  const [ticketAmountError, setTicketAmountError] = useState(false);

  // State for lot settings validation
  const [lotNameError, setLotNameError] = useState(false);
  const [companyNameError, setCompanyNameError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [lotCapacityError, setLotCapacityError] = useState(false);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add dirty state context
  const { setIsDirty: setGlobalDirty } = useDirtyState();

  // Update useEffect to sync local and global dirty state
  useEffect(() => {
    setGlobalDirty(isDirty);
  }, [isDirty, setGlobalDirty]);

  // Add cleanup effect to clear dirty state when component unmounts
  useEffect(() => {
    return () => {
      setGlobalDirty(false);
    };
  }, [setGlobalDirty]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current !== null) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);
  const copyToClipboard = useCallback((value?: string) => {
    if (!value) return;
    const fallbackCopy = () => {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch (error) {
        console.error("Failed to copy text", error);
      }
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleModeChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled: boolean }>).detail;
      setIsSuperadminModeOn(detail.enabled);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === SUPERADMIN_MODE_STORAGE_KEY) {
        setIsSuperadminModeOn(event.newValue === "true");
      }
    };

    window.addEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  // Helper function to limit input to 2 decimal places
  const limitToTwoDecimalPlaces = (value: string): string => {
    // Remove any non-numeric characters except the decimal point
    let sanitized = value.replace(/[^\d.]/g, '');

    // Handle multiple decimal points - keep only the first one
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      sanitized = parts[0] + '.' + parts.slice(1).join('');
    }

    // Then check if it has more than 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
      return parts[0] + '.' + parts[1].substring(0, 2);
    }

    return sanitized;
  };

  const buildLotUpdatePayload = (latestLotData: any, overrides?: Partial<{ allowBetaAi: boolean; developmentLot: boolean; lotStatus: string }>) => {
    const nextAllowBetaAi = overrides?.allowBetaAi ?? (latestLotData.allowBetaAi ?? allowBetaAi ?? false);
    const nextDevelopmentLot = overrides?.developmentLot ?? (latestLotData.developmentLot ?? developmentLot ?? false);
    const nextLotStatus = overrides?.lotStatus ?? latestLotData.lotStatus ?? lotStatus ?? "Active";

    return {
      ownerId: latestLotData.ownerId,
      companyName: latestLotData.companyName,
      address: latestLotData.address,
      lotName: latestLotData.lotName,
      lotCapacity: parseInt(String(latestLotData.lotCapacity)) || 0,
      hasEvCharging: latestLotData.hasEvCharging ?? false,
      modifiedBy: user?.userId || latestLotData.ownerId,
      lotStatus: nextLotStatus,
      allowBetaAi: nextAllowBetaAi,
      developmentLot: nextDevelopmentLot,
    };
  };

  const applyLotUpdate = async (overrides: Partial<{ allowBetaAi: boolean; developmentLot: boolean; lotStatus: string }>) => {
    if (!lotId || !lotData) return;
    const latestLotData = contextLotData || lotData;
    const payload = buildLotUpdatePayload(latestLotData, overrides);
    await lotService.updateLot(lotId, payload);
    setLotData({
      ...latestLotData,
      ...overrides,
    });
    invalidateData();
    fetchUserLots();
  };

  // Function to load lot pricing data
  const loadLotPricing = async () => {
    if (!lotId) return;

    try {
      setIsLoading(true);
      const pricingData = await lotPricingService.getLatestPricingByLotId(lotId);
      setLotPricingData(pricingData);

      if (pricingData) {
        // Set state from fetched pricing data, handling null values
        setHourlyPrice(pricingData.hourlyRate?.toString() || "");
        setDailyMaxPrice(pricingData.dailyMaximumPrice?.toString() || "");

        // Convert grace period from seconds to minutes, default to 10 if null
        const gracePeriodSeconds = pricingData.gracePeriod || 600; // Default to 10 minutes in seconds
        const gracePeriodMinutes = Math.floor(gracePeriodSeconds / 60);
        setGracePeriod(gracePeriodMinutes.toString());

        // Maximum time is already in hours, handle null case
        const maxTimeHours = pricingData.maximumTime || 0;
        setMaxTime(maxTimeHours > 0 ? maxTimeHours.toString() : "");

        setTicketAmount(pricingData.ticketAmount?.toString() || "");
        setFreeParking(pricingData.freeParking || false);
        setAllowValidation(pricingData.allowValidation || false);
      }
    } catch (error) {
      console.error("Error loading lot pricing:", error);
      // Set default values if fetch fails
      setGracePeriod("10"); // Default grace period is 10 minutes
    } finally {
      setIsLoading(false);
      setIsDirty(false); // Reset dirty state after loading data
    }
  };

  // Fetch lot data when component mounts
  useEffect(() => {
    const fetchLotData = async () => {
      try {
        if (!lotId) return;
        const fetchedLot = await lotService.getLotById(lotId);
        setLotData(fetchedLot);
        setLotName(fetchedLot.lotName || "Unknown Lot");
        setCompanyName(fetchedLot.companyName || "Unknown Company");
        setAddress(fetchedLot.address || "Unknown Address");
        setLotCapacity(String(fetchedLot.lotCapacity ?? "0"));
        setRegistryOn(fetchedLot.registryOn !== undefined ? fetchedLot.registryOn : false);
        setOwnerCustomerId(fetchedLot.ownerId || customerId);
        setAddressLastUpdatedOn(fetchedLot.addressLastUpdatedOn || null);
        setLotDisplayId(fetchedLot.displayLotId || "");
        setLotStatus(fetchedLot.lotStatus || fetchedLot.accountStatus || "Active");
        setLotCreatedOn(fetchedLot.createdOn || null);
        setLotModifiedOn(fetchedLot.modifiedOn || null);
        setAllowBetaAi(fetchedLot.allowBetaAi === true);
        setDevelopmentLot(fetchedLot.developmentLot === true);
        setLotLatitude(parseCoordinate(fetchedLot.lotLatitude ?? fetchedLot.geoLocation?.lat));
        setLotLongitude(parseCoordinate(fetchedLot.lotLongitude ?? fetchedLot.geoLocation?.long));

        // Calculate if address is locked
        if (fetchedLot.addressLastUpdatedOn) {
          const lastUpdated = new Date(fetchedLot.addressLastUpdatedOn);
          const now = new Date();
          const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
          const timeDifference = now.getTime() - lastUpdated.getTime();

          if (timeDifference < twentyFourHours) {
            setIsAddressLocked(true);
            const timeLeft = twentyFourHours - timeDifference;
            const hours = Math.floor(timeLeft / (1000 * 60 * 60));
            const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));

            if (timeLeft > 0 && hours === 0 && minutes === 0) {
              setRemainingTime("less than a minute");
            } else {
              setRemainingTime(`${hours > 0 ? `${hours} hour${hours > 1 ? 's' : ''}` : ''}${hours > 0 && minutes > 0 ? ' ' : ''}${minutes > 0 ? `${minutes} min${minutes > 1 ? 's' : ''}` : ''}`);
            }
          } else {
            setIsAddressLocked(false);
            setRemainingTime("");
          }
        } else {
          setIsAddressLocked(false);
          setRemainingTime("");
        }

        // Load lot pricing data
        await loadLotPricing();
      } catch (error) {
        console.error('Error fetching lot data:', error);
        // Set default values if fetch fails
        setLotName("Unknown Lot");
        setCompanyName("Unknown Company");
        setAddress("Unknown Address");
        setLotCapacity("0");
      }
    };

    fetchLotData();
  }, [lotId]);

  // Update local lot info when contextLotData changes (e.g., after an update)
  useEffect(() => {
    if (contextLotData) {
      setLotName(contextLotData.lotName || "Unknown Lot");
      setCompanyName(contextLotData.companyName || "Unknown Company");
      setAddress(contextLotData.address || "Unknown Address");
      setLotCapacity(String(contextLotData.lotCapacity ?? "0"));
      setRegistryOn(contextLotData.registryOn !== undefined ? contextLotData.registryOn : false);
      setLotStatus((prev) => contextLotData.lotStatus || contextLotData.accountStatus || prev);
      setAllowBetaAi(contextLotData.allowBetaAi === true);
      setDevelopmentLot(contextLotData.developmentLot === true);
      setLotDisplayId((prev) => contextLotData.displayLotId || prev);
      setLotCreatedOn((prev) => contextLotData.createdOn || prev);
      setLotModifiedOn((prev) => contextLotData.modifiedOn || prev);
      setLotLatitude(parseCoordinate(contextLotData.lotLatitude ?? contextLotData.geoLocation?.lat));
      setLotLongitude(parseCoordinate(contextLotData.lotLongitude ?? contextLotData.geoLocation?.long));
      // Also update the full lotData object to keep everything in sync
      setLotData(contextLotData);
    }
  }, [contextLotData]);

  useEffect(() => {
    setSaveButtonOpacity(isDirty ? 1 : 0.3);
  }, [isDirty]);

  // Parse address into components for editing
  const parseAddressForEditing = (fullAddress: string) => {
    const parts = fullAddress.split(', ');
    if (parts.length >= 5) {
      setStreetNumber(parts[0] || "");
      setStreetName(parts[1] || "");
      setCity(parts[2] || "");
      setProvinceState(parts[3] || "");
      setPostalCode(parts[4] || "");
      setCountry(parts[5] || "Canada");
    } else {
      // If address doesn't match expected format, try to parse what we can
      setStreetNumber("");
      setStreetName("");
      setCity("");
      setProvinceState("");
      setPostalCode("");
      setCountry("Canada");
    }
  };

  // Validate address components
  const validateStreetNumber = (value: string): boolean => {
    if (value.trim().length === 0) return true; // Don't show error for empty fields
    return /^[0-9]+$/.test(value.trim()) && value.length <= 10;
  };

  const validateStreetName = (value: string): boolean => {
    if (value.trim().length === 0) return true; // Don't show error for empty fields
    return value.length <= 50;
  };

  const validateCity = (value: string): boolean => {
    if (value.trim().length === 0) return true; // Don't show error for empty fields
    return value.length <= 30;
  };

  const validatePostalCode = (value: string): boolean => {
    if (value.trim().length === 0) return true; // Don't show error for empty fields
    if (country === "Canada") {
      // Canadian postal code format: A1A 1A1
      return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(value);
    } else {
      // US ZIP code format: 12345 or 12345-6789
      return /^\d{5}(-\d{4})?$/.test(value);
    }
  };

  const addressRegionOptions = country === "Canada" ? canadianProvinces : usStates;
  const selectedProvinceStateLabel = addressRegionOptions.find(
    (option) => option.value === provinceState
  )?.label || `Select ${country === "Canada" ? "Province" : "State"}`;

  const countryMenuItems = [
    {
      key: "canada",
      label: "Canada",
      selected: country === "Canada",
      onSelect: () => {
        if (country === "Canada") return;
        setCountry("Canada");
        setProvinceState("");
        setProvinceStateError(false);
        setIsDirty(true);
      },
    },
    {
      key: "united-states",
      label: "United States",
      selected: country === "United States",
      onSelect: () => {
        if (country === "United States") return;
        setCountry("United States");
        setProvinceState("");
        setProvinceStateError(false);
        setIsDirty(true);
      },
    },
  ];

  const provinceStateMenuItems = addressRegionOptions.map((option) => ({
    key: option.value,
    label: option.label,
    selected: provinceState === option.value,
    onSelect: () => {
      if (provinceState === option.value) return;
      setProvinceState(option.value);
      setProvinceStateError(false);
      setIsDirty(true);
    },
  }));

  // Open popup for Lot Settings
  const openEditPopup = (field: string, value: string) => {
    if (freeParking) {
      setModalType("freeParking");
      return;
    }

    if (field === "address") {
      if (developmentLot && !canUseSuperadminTools) {
        setModalType("demoLotAddressBlocked");
        return;
      }
      parseAddressForEditing(value);
      setAddressModalOpen(true);
    } else {
      setEditingField(field);
      setTempValue(value);
    }
  };

  const saveAddressChanges = async () => {
    if (!lotId || !lotData) return;

    try {
      // Combine address components with commas
      const combinedAddress = [
        streetNumber.trim(),
        streetName.trim(),
        city.trim(),
        provinceState,
        postalCode.trim(),
        country
      ].join(', ');

      console.log("Sending update-lot request for address:", combinedAddress);

      const latestLotData = contextLotData || lotData;

      const updatedLot = {
        ownerId: latestLotData.ownerId,
        companyName: latestLotData.companyName,
        address: combinedAddress,
        lotName: latestLotData.lotName,
        lotCapacity: parseInt(String(latestLotData.lotCapacity)) || 0,
        hasEvCharging: false,
        modifiedBy: latestLotData.ownerId,
        lotStatus: latestLotData.lotStatus ?? lotStatus ?? "Active",
        allowBetaAi: latestLotData.allowBetaAi ?? allowBetaAi ?? false,
        developmentLot: latestLotData.developmentLot ?? developmentLot ?? false
      };

      console.log("Updated lot object:", updatedLot);
      await lotService.updateLot(lotId, updatedLot);
      console.log("Successfully updated lot address");

      setLotData({
        ...latestLotData,
        address: combinedAddress
      });

      setAddressModalOpen(false);
      setIsDirty(false);

      invalidateData();
      fetchUserLots();

    } catch (error) {
      console.error("Error updating lot:", error);
      alert("Failed to update lot address.");
    }
  };

  const saveEditPopup = async () => {
    if (!editingField || !lotId || !lotData) return;

    try {
      console.log("Sending update-lot request for field:", { [editingField]: tempValue });

      // Create update object with only required fields - but ensure we have the latest lot data first
      const latestLotData = contextLotData || lotData; // Use context data if available (most up-to-date)

      const updatedLot = {
        ownerId: latestLotData.ownerId,
        companyName: editingField === "companyName" ? tempValue : latestLotData.companyName,
        address: editingField === "address" ? tempValue : latestLotData.address,
        lotName: editingField === "lotName" ? tempValue : latestLotData.lotName,
        lotCapacity: editingField === "lotCapacity"
          ? (tempValue.trim() === "" ? (parseInt(String(latestLotData.lotCapacity)) || 0) : parseInt(tempValue))
          : (parseInt(String(latestLotData.lotCapacity)) || 0),
        hasEvCharging: false, // Default to false as per requirements
        modifiedBy: latestLotData.ownerId, // Using ownerId as modifiedBy
        lotStatus: latestLotData.lotStatus ?? lotStatus ?? "Active", // Preserve current status
        allowBetaAi: latestLotData.allowBetaAi ?? allowBetaAi ?? false, // Preserve current value
        developmentLot: latestLotData.developmentLot ?? developmentLot ?? false // Preserve current value
      };

      console.log("Updated lot object:", updatedLot);
      await lotService.updateLot(lotId, updatedLot);
      console.log("Successfully updated lot settings");

      // Update local state with the new values to keep in sync
      setLotData({
        ...latestLotData,
        [editingField]: editingField === "lotCapacity"
          ? (tempValue.trim() === "" ? (parseInt(String(latestLotData.lotCapacity)) || 0) : parseInt(tempValue))
          : tempValue
      });

      // Update UI state
      setEditingField(null);
      setModalType(null);
      setIsDirty(false);

      // Invalidate the lot context data to force a refresh in all components using the context
      invalidateData();
      // Also refresh userLots so sidebar dropdown and LotDashboard show updated name
      fetchUserLots();

    } catch (error) {
      console.error("Error updating lot:", error);
      alert("Failed to update lot settings.");
    }
  };

  const updateAdvancedFlag = async (field: "allowBetaAi" | "developmentLot", nextValue: boolean) => {
    const previousValue = field === "allowBetaAi" ? allowBetaAi : developmentLot;
    if (!lotId || !lotData || previousValue === nextValue) {
      return;
    }

    if (field === "allowBetaAi") {
      setAllowBetaAi(nextValue);
      setIsBetaUpdating(true);
    } else {
      setDevelopmentLot(nextValue);
      setIsDevelopmentUpdating(true);
    }

    try {
      await applyLotUpdate({ [field]: nextValue } as Partial<{ allowBetaAi: boolean; developmentLot: boolean; lotStatus: string }>);
    } catch (error) {
      console.error(`Failed to update ${field}`, error);
      alert(`Failed to update ${field === "allowBetaAi" ? "Allow Beta AI" : "Demo Lot"}.`);
      if (field === "allowBetaAi") {
        setAllowBetaAi(previousValue);
      } else {
        setDevelopmentLot(previousValue);
      }
    } finally {
      if (field === "allowBetaAi") {
        setIsBetaUpdating(false);
      } else {
        setIsDevelopmentUpdating(false);
      }
    }
  };

  const handleBetaAiToggle = (nextValue: boolean) => {
    if (!canUseSuperadminTools || isBetaUpdating) return;
    if (nextValue) {
      setPendingAdvancedToggle({ field: "allowBetaAi", value: nextValue });
    } else {
      updateAdvancedFlag("allowBetaAi", nextValue);
    }
  };

  const handleDevelopmentToggle = (nextValue: boolean) => {
    if (!canUseSuperadminTools || isDevelopmentUpdating) return;
    if (nextValue) {
      setPendingAdvancedToggle({ field: "developmentLot", value: nextValue });
    } else {
      updateAdvancedFlag("developmentLot", nextValue);
    }
  };

  const handleArchiveToggle = async () => {
    if (!canUseSuperadminTools || isStatusUpdating) return;
    if (!lotId || !lotData) return;

    const previousStatus = lotStatus;
    const nextStatus = lotStatus === "Suspended" ? "Active" : "Suspended";
    setLotStatus(nextStatus);
    setIsStatusUpdating(true);

    try {
      await applyLotUpdate({ lotStatus: nextStatus });
    } catch (error) {
      console.error("Failed to update lot status", error);
      alert("Failed to update lot status.");
      setLotStatus(previousStatus);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const handleDeleteLot = async () => {
    if (!canUseSuperadminTools || isDeletingLot) return;
    if (!lotId || !lotData) return;

    if (lotStatus !== "Suspended") {
      alert("Only archived lots can be deleted.");
      return;
    }
    if ((lotData?.currentOccupancy ?? 0) > 0) {
      alert("Lots with current occupancy cannot be deleted.");
      return;
    }

    setIsDeletingLot(true);
    try {
      await lotService.deleteLot(lotId, user?.userId || user?.email || "superadmin");
      setDeleteModalOpen(false);
      invalidateData();
      await fetchUserLots();
      navigate("/lot-dashboard");
    } catch (error: any) {
      console.error("Failed to delete lot", error);
      alert(error?.response?.data?.message || "Failed to delete lot.");
    } finally {
      setIsDeletingLot(false);
    }
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSaveSettings = async () => {
    if (!lotId || !lotPricingData) return;

    const isMaxTimeEmpty = maxTime.trim() === ""; // Check if Max Time is blank

    try {
      setIsSaving(true);
      // Create the update payload with proper null handling and type conversion
      const updatedPricing = {
        hourlyRate: hourlyPrice.trim() === "" ? 0.01 : parseFloat(hourlyPrice),
        dailyMaximumPrice: dailyMaxPrice.trim() === "" ? 0.01 : parseFloat(dailyMaxPrice),
        gracePeriod: gracePeriod.trim() === "" ? 600 : Math.max(parseInt(gracePeriod) * 60, 600), // Convert to seconds, ensure minimum 10 minutes
        maximumTime: isMaxTimeEmpty ? 0 : Math.max(parseInt(maxTime), 1), // Keep in hours, ensure minimum 1 hour, or 0 if empty
        ticketAmount: isMaxTimeEmpty ? null : ticketAmount.trim() === "" ? null : parseFloat(ticketAmount), // Allow null for empty values
        freeParking: freeParking,
        allowValidation: allowValidation,
        createdBy: user?.userId || "system" // Use current user's ID or fallback to "system"
      };

      console.log("Sending update request with data:", updatedPricing);

      // Use the current pricingId for the update
      await lotPricingService.updatePricing(lotId, lotPricingData.pricingId, updatedPricing);

      // Reload pricing data after saving to get the new active pricing
      await loadLotPricing();

      setIsDirty(false);
    } catch (error) {
      console.error("Error updating lot pricing:", error);
      alert("Failed to update pricing settings.");
    } finally {
      setIsSaving(false);
    }
  };

  // Track changes to form fields
  useEffect(() => {
    if (lotPricingData) {
      const hasChanges =
        (hourlyPrice !== (lotPricingData.hourlyRate?.toString() || "")) ||
        (dailyMaxPrice !== (lotPricingData.dailyMaximumPrice?.toString() || "")) ||
        (gracePeriod !== (Math.floor((lotPricingData.gracePeriod || 600) / 60)).toString()) ||
        (maxTime !== (lotPricingData.maximumTime ? lotPricingData.maximumTime.toString() : "")) ||
        (ticketAmount !== (lotPricingData.ticketAmount?.toString() || "")) ||
        (freeParking !== lotPricingData.freeParking) ||
        (allowValidation !== lotPricingData.allowValidation);

      setIsDirty(hasChanges);
    }
  }, [
    hourlyPrice,
    dailyMaxPrice,
    gracePeriod,
    maxTime,
    ticketAmount,
    freeParking,
    allowValidation,
    lotPricingData,
    maxTimeMinError
  ]);

  // Handle navigation attempts with unsaved changes
  const handleNavigation = (path: string) => {
    if (isDirty) {
      setPendingAction(() => () => navigate(path));
      setModalType("unsavedChanges");
    } else {
      navigate(path);
    }
  };

  const formattedLotId = formatDisplayLotId(lotDisplayId);
  const coordinateLabel =
    lotLatitude !== null && lotLongitude !== null
      ? `${lotLatitude.toFixed(4)}, ${lotLongitude.toFixed(4)}`
      : "N/A";
  const coordinateLink =
    lotLatitude !== null && lotLongitude !== null
      ? `https://www.google.com/maps/search/?api=1&query=${lotLatitude},${lotLongitude}`
      : null;
  const metadataTooltipContent = (
    <div className="lot-tooltip-grid">
      <div className="lot-tooltip-row">
        <span className="lot-tooltip-heading">Purchase Date</span>
        <span>{formatReadableDate(lotCreatedOn)}</span>
      </div>
      <div className="lot-tooltip-row">
        <span className="lot-tooltip-heading">Coordinates</span>
        {coordinateLink ? (
          <a className="lot-tooltip-link" href={coordinateLink} target="_blank" rel="noopener noreferrer">
            {coordinateLabel}
          </a>
        ) : (
          <span>{coordinateLabel}</span>
        )}
      </div>
      {canUseSuperadminTools && (
        <>
          <div className="lot-tooltip-row">
            <span className="lot-tooltip-heading">Lot ID</span>
            <span className="lot-tooltip-mono">{lotId}</span>
          </div>
          <div className="lot-tooltip-row">
            <span className="lot-tooltip-heading">Modified</span>
            <span>{formatReadableDate(lotModifiedOn, { includeTime: true })}</span>
          </div>
          <div className="lot-tooltip-row">
            <span className="lot-tooltip-heading">Address Updated</span>
            <span>{formatReadableDate(addressLastUpdatedOn, { includeTime: true })}</span>
          </div>
        </>
      )}
    </div>
  );

  const baseLotToggleOptions = [
    {
      label: "Free Parking",
      value: freeParking,
      onToggle: () => {
        setFreeParking(!freeParking);
        setIsDirty(true);
      },
      tooltip: "All occupants that enter and are currently parked will not be billed for their sesision and will not be automatically ticketed",
      disableOnFreeParking: false,
      disabled: false
    },
    {
      label: "Allow Validation",
      value: allowValidation,
      onToggle: () => {
        setAllowValidation(!allowValidation);
        setIsDirty(true);
      },
      tooltip: "Allows operators to validate vehicles manually and lets users request validation via the app.",
      disableOnFreeParking: true,
      disabled: false
    }
  ];

  const superadminToggleOptions = canUseSuperadminTools
    ? [
        {
          label: "Allow Beta AI",
          value: allowBetaAi,
          onToggle: () => handleBetaAiToggle(!allowBetaAi),
          tooltip: "Enable experimental Parallel AI features for this lot.",
          disabled: isBetaUpdating,
        },
        {
          label: "Demo Lot",
          value: developmentLot,
          onToggle: () => handleDevelopmentToggle(!developmentLot),
          tooltip: "Unlock demo tooling like Generate Data for this account.",
          disabled: isDevelopmentUpdating,
        },
      ]
    : [];

  const handleCopyLotId = () => {
    const copyTarget = lotDisplayId ? formattedLotId : lotId;
    if (!copyTarget) return;
    copyToClipboard(copyTarget);
    setHasCopiedLotId(true);
    if (copyTimeoutRef.current !== null) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => setHasCopiedLotId(false), 500);
  };

  if (isLoading) {
    return (
      <div className="settings-loading">
        <div className="lot-settings-header">
          <h1>Lot Settings</h1>
          <div className="skeleton-block settings-skeleton-lot-id" aria-hidden="true" />
        </div>
        <p>General lot information used for identification, capacity, and timezone. <span className="tutorial-link">Tutorial Video</span></p>

        <div className="settings-grid">
          <div className="setting-item">
            <b>Lot Name:</b>
            <div className="skeleton-block settings-skeleton-text" aria-hidden="true" />
          </div>
          <div className="setting-item">
            <b>Company Name:</b>
            <div className="skeleton-block settings-skeleton-text" aria-hidden="true" />
          </div>
          <div className="setting-item">
            <b>Address:</b>
            <div className="skeleton-block settings-skeleton-text" aria-hidden="true" />
          </div>
          <div className="setting-item">
            <b>Lot Capacity:</b>
            <div className="skeleton-block settings-skeleton-text" aria-hidden="true" />
          </div>
        </div>

        <h1 className="margin-top">Pricing Settings</h1>
        <p>Basic settings to calculate the amount to bill visitors.</p>

        <div className="pricing-grid">
          {["Hourly Price", "Daily Maximum Price", "Grace Period", "Maximum Hours", "Ticket Amount"].map((label) => (
            <div className="settings-input-group" key={label}>
              <label className="settings-label">{label}</label>
              <div className="skeleton-block settings-skeleton-input" aria-hidden="true" />
            </div>
          ))}
        </div>

        <div className="settings-toggle-container">
          <div className="toggle-group">
            <span className="settings-label">Free Parking</span>
            <div className="skeleton-block settings-skeleton-toggle" aria-hidden="true" />
          </div>
          <div className="toggle-group">
            <span className="settings-label">Allow Validation</span>
            <div className="skeleton-block settings-skeleton-toggle" aria-hidden="true" />
          </div>
        </div>

        <div className="settings-skeleton-buttons">
          <div className="skeleton-block settings-skeleton-button" aria-hidden="true" />
          <div className="skeleton-block settings-skeleton-button secondary" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (error) {
    return <Error message={error} />;
  }

  return (
    <div data-walkthrough="settings-page">
      {/* Mobile Save Button (top, only visible on mobile) */}
      <button
        className="button-primary button-icon mobile-save-button"
        style={{
          opacity:
            !gracePeriodError &&
              !hourlyPriceError &&
              !dailyMaxPriceError &&
              !dailyMaxPriceEmptyError &&
              !ticketAmountError &&
              !maxTimeMinError &&
              isDirty
              ? 1
              : 0.3,
          marginBottom: 16,
          marginTop: 8,
          width: '100%',
          display: 'none', // default hidden, CSS will show on mobile
        }}
        disabled={
          gracePeriodError ||
          hourlyPriceError ||
          dailyMaxPriceError ||
          dailyMaxPriceEmptyError ||
          ticketAmountError ||
          maxTimeMinError ||
          !isDirty
        }
        onClick={() => {
          const inWalkthrough =
            typeof document !== "undefined" &&
            document.body.classList.contains("walkthrough-active");
          if (inWalkthrough) {
            handleSaveSettings();
            setIsAcknowledged(false);
            return;
          }
          if (freeParking) {
            setModalType("confirmFreeParking");
          } else {
            setModalType("confirmSave");
          }
        }}
      >
        <img
          src={
            !gracePeriodError &&
              !hourlyPriceError &&
              !dailyMaxPriceError &&
              !dailyMaxPriceEmptyError &&
              !ticketAmountError &&
              !maxTimeMinError &&
              isDirty
              ? "/assets/Save.svg"
              : "/assets/SaveDisabled.svg"
          }
          alt="Save"
        />
        <span>Save</span>
      </button>
      <div className="lot-settings-header">
        <h1>
          Lot Settings{" "}
          <span className={`lot-settings-id ${hasCopiedLotId ? "is-copied" : ""}`}>
            #{formattedLotId}
          </span>
          <button
            type="button"
            className="copy-button lot-settings-copy-button"
            onClick={handleCopyLotId}
            aria-label={hasCopiedLotId ? "Lot ID copied" : "Copy Lot ID"}
            title={hasCopiedLotId ? "Copied!" : "Copy Lot ID"}
          >
            <img
              src={hasCopiedLotId ? "/assets/copy-check.svg" : "/assets/copy.svg"}
              alt={hasCopiedLotId ? "Copied" : "Copy"}
            />
          </button>
        </h1>
        <Tooltip content={metadataTooltipContent} position="right" persistentOnClick>
          <img src="/assets/info.svg" alt="Lot details" className="lot-settings-info" />
        </Tooltip>
      </div>
      <p>General lot information used for identification, capacity, and timezone. <span className="tutorial-link" onClick={() => {
        setModalType("tutorialVideo");
        setModalOpen(true);
      }}>Tutorial Video</span></p>

      <div className={`settings-grid ${freeParking ? "disabled" : ""}`}>
        {[
          { label: "Lot Name", value: lotName, field: "lotName" },
          { label: "Company Name", value: companyName, field: "companyName" },
          { label: "Address", value: address, field: "address" },
          { label: "Lot Capacity", value: lotCapacity, field: "lotCapacity" },
        ].map(({ label, value, field }) => (
          <div className="setting-item" key={field}>
            <b>{label}:</b>
            <div className="setting-value">
              <span>{value}</span>
              <div className="settings-edit-container">
                <Tooltip text="Edit" position="right" size="small" delayMs={600}>
                  <img
                    src="/assets/Edit.svg"
                    alt="Edit"
                    className="edit-icon"
                    onClick={() => {
                      if (freeParking) {
                        setModalType("freeParking");
                      } else {
                        openEditPopup(field, value);
                      }
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          </div>
        ))}
      </div>
      {superadminToggleOptions.length > 0 && (
        <>
          <h1 className="margin-top">Super Settings</h1>
          <div className="settings-toggle-container lot-settings-toggle-row">
            {superadminToggleOptions.map(({ label, value, onToggle, tooltip, disabled }) => (
              <div className="toggle-group" key={label}>
                <span className="settings-label">
                  {label} {tooltip && <Tooltip text={tooltip} />}
                </span>
                <Slider
                  checked={value}
                  onChange={() => {
                    if (disabled) return;
                    onToggle();
                  }}
                  disabled={Boolean(disabled)}
                />
              </div>
            ))}
          </div>
        </>
      )}

      <h1 className="margin-top">Pricing Settings</h1>
      <p>Basic settings to calculate the amount to bill visitors.</p>

      <div className="pricing-grid" style={{ opacity: freeParking ? 0.5 : 1 }}>
        {[
          { label: "Hourly Price", value: hourlyPrice, setValue: setHourlyPrice, unit: "per hour" },
          { label: "Daily Maximum Price", value: dailyMaxPrice, setValue: setDailyMaxPrice, unit: "per day" },
          {
            label: "Grace Period",
            value: gracePeriod,
            setValue: setGracePeriod,
            unit: "mins",
            tooltip: "Time in lot before a car is charged. Minimum: 10 minutes.",
            placeholder: "10"
          },
          {
            label: "Maximum Hours",
            value: maxTime,
            setValue: (val: string) => {
              const newValue = val === "0" || val.toLowerCase() === "none" ? "" : val;
              setMaxTime(newValue);
              setIsDirty(true);
            },
            unit: "hours",
            tooltip: "Time before issuing a ticket or alerting management. Minimum: 1 hour.",
            placeholder: "none"
          },
          {
            label: "Ticket Amount",
            value: ticketAmount,
            setValue: setTicketAmount,
            unit: "fee",
            tooltip: "Once a vehicle exceeds Maximum Hours, a ticket is automatically issued.",
            disabled: maxTime.trim() === "" // Disable if Max Time is blank
          }
        ].map(({ label, value, setValue, unit, tooltip, placeholder, disabled }) => (
          <div className="settings-input-group" key={label} style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto" }}>
            <label className="settings-label">
              {label} {tooltip && <Tooltip text={tooltip} />}
            </label>
            <div className="settings-input-wrapper" style={{ position: "relative" }}>
              <input
                type="text"
                inputMode="decimal"
                value={disabled ? "" : value} // Clear input when faded out
                onChange={(e) => {
                  // Different validation based on field type
                  if (label === "Grace Period") {
                    const newValue = e.target.value.replace(/[^0-9]/g, ""); // Allow only whole numbers
                    setGracePeriod(newValue);
                    setGracePeriodError(parseInt(newValue) < 10); // Show error if less than 10
                  } else if (label === "Maximum Hours") {
                    // Allow decimal values for Maximum Time
                    const formattedValue = limitToTwoDecimalPlaces(e.target.value);
                    // Prevent starting with a decimal if the input is currently empty
                    if (value === "" && formattedValue === ".") {
                      setMaxTime(""); // Keep it empty
                    } else {
                      setMaxTime(formattedValue);
                      // Validate if the value is less than 1 after formatting
                      const parsedValue = formattedValue.trim() === "" ? 0 : parseFloat(formattedValue);
                      setMaxTimeMinError(parsedValue > 0 && parsedValue < 1);
                    }
                    setMaxTimeError(false); // Maximum Time has no min/max restrictions
                  } else if (label === "Hourly Price") {
                    // For hourly price, validate max value and prevent exceeding daily max
                    const formattedValue = limitToTwoDecimalPlaces(e.target.value);
                    const parsedValue = formattedValue === "" ? 0 : parseFloat(formattedValue);
                    const dailyMaxValue = parseFloat(dailyMaxPrice || "0");

                    // Prevent hourly price from exceeding daily maximum price
                    if (formattedValue && dailyMaxValue && parsedValue > dailyMaxValue) {
                      return; // Don't allow the change
                    }

                    setHourlyPrice(formattedValue);
                    setHourlyPriceError(parsedValue > 100);
                  } else if (label === "Daily Maximum Price") {
                    // For daily max price, validate max value and adjust hourly price if needed
                    const formattedValue = limitToTwoDecimalPlaces(e.target.value);
                    const parsedValue = formattedValue === "" ? 0 : parseFloat(formattedValue);
                    const currentHourlyValue = parseFloat(hourlyPrice || "0");

                    // Check if daily maximum price is empty
                    setDailyMaxPriceEmptyError(formattedValue.trim() === "");

                    // If new daily max is less than current hourly price, adjust the hourly price down
                    if (formattedValue && currentHourlyValue && parsedValue < currentHourlyValue) {
                      setHourlyPrice(formattedValue);
                    }

                    setDailyMaxPrice(formattedValue);
                    setDailyMaxPriceError(parsedValue > 100);
                  } else if (label === "Ticket Amount" && !disabled) {
                    // For ticket amount, validate max value
                    const formattedValue = limitToTwoDecimalPlaces(e.target.value);
                    setTicketAmount(formattedValue);
                    const parsedValue = formattedValue === "" ? 0 : parseFloat(formattedValue);
                    setTicketAmountError(parsedValue > 250);
                  } else if (!disabled) {
                    // Fallback for any other fields
                    const formattedValue = limitToTwoDecimalPlaces(e.target.value);
                    setValue(formattedValue);
                  }

                  setIsDirty(true);
                }}
                onKeyDown={(e) => {
                  // Allow: backspace, delete, tab, escape, enter, decimal point, and numbers
                  if (
                    [8, 9, 13, 27, 46, 110, 190].indexOf(e.keyCode) !== -1 ||
                    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                    (e.keyCode >= 35 && e.keyCode <= 39) ||
                    // Allow: home, end, left, right
                    (e.ctrlKey && [65, 67, 86, 88].indexOf(e.keyCode) !== -1) ||
                    // Allow numbers
                    (/[0-9]/.test(e.key))
                  ) {
                    // Check if it's a decimal point
                    if (e.key === '.' && label === "Grace Period") {
                      e.preventDefault(); // Prevent decimal for Grace Period
                    } else if (e.key === '.' && value.includes('.')) {
                      e.preventDefault(); // Prevent multiple decimal points
                    } else {
                      return; // Allow the keystroke
                    }
                  } else {
                    // Prevent the keystroke
                    e.preventDefault();
                  }
                }}
                placeholder={disabled ? "0" : value === "" ? placeholder || "0" : ""}
                disabled={disabled} // Disable input if Ticket Amount is faded
                style={label === "Hourly Price" || label === "Daily Maximum Price" || label === "Ticket Amount" ? { paddingLeft: "35px" } : undefined}
              />
              {(label === "Hourly Price" || label === "Daily Maximum Price" || label === "Ticket Amount") && <span style={{ position: "absolute", left: "20px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--text-medium)" }}>$</span>}
              <span className="settings-input-unit">{unit}</span>
            </div>
          </div>
        ))}

      </div>
      <div className="settings-toggle-container">
        {baseLotToggleOptions.map(({ label, value, onToggle, disableOnFreeParking = false, tooltip }) => (
          <div
            className="toggle-group"
            key={label}
            style={{ opacity: freeParking && disableOnFreeParking ? 0.5 : 1, pointerEvents: freeParking && disableOnFreeParking ? "none" : "auto" }}
          >
            <span className="settings-label">
              {label} {tooltip && <Tooltip text={tooltip} />}
            </span>
            <Slider
              checked={value}
              onChange={() => {
                onToggle();
              }}
              disabled={freeParking && disableOnFreeParking}
            />
          </div>
        ))}
      </div>

      {/*  Place the message outside of the toggle-container to appear below */}
      {freeParking && (
        <p className="free-parking-message">
          <em>When free parking is turned on, the cameras will still record data and be running; however, billing will be disabled for your lot until turned back on.</em>
        </p>
      )}


      <div className="button-group">
        <button
          data-walkthrough="settings-save"
          className="button-primary button-icon"
          style={{ opacity: saveButtonOpacity }}
          disabled={!isDirty}
          onClick={() => {
            const inWalkthrough =
              typeof document !== "undefined" &&
              document.body.classList.contains("walkthrough-active");
            if (inWalkthrough) {
              handleSaveSettings();
              setIsAcknowledged(false);
              return;
            }
            if (freeParking) {
              setModalType("confirmFreeParking");
            } else {
              setModalType("confirmSave");
            }
          }}
        >
          <img src={isDirty ? "/assets/Save.svg" : "/assets/SaveDisabled.svg"} alt="Save" />
          <span>Save</span>
        </button>



        <button
          className="button-secondary button-icon"
          onClick={() => setIsQrModalOpen(true)}
        >
          <img src="/assets/qr-code.svg" alt="Download QR code" />
          <span>Download QR Code</span>
        </button>

        {canUseSuperadminTools && (
          <button
            className="button-secondary"
            onClick={handleArchiveToggle}
            disabled={isStatusUpdating}
          >
            {isStatusUpdating ? "Saving..." : lotStatus === "Suspended" ? "Unarchive" : "Archive"}
          </button>
        )}
        {canUseSuperadminTools && lotStatus === "Suspended" && (
          <button className="button-danger" onClick={() => setDeleteModalOpen(true)} disabled={isDeletingLot}>
            Delete
          </button>
        )}
      </div>
      {(gracePeriodError || maxTimeError || hourlyPriceError || dailyMaxPriceError || dailyMaxPriceEmptyError || ticketAmountError || maxTimeMinError) && (
        <p className="error">
          <img src="/assets/error.svg" alt="Error icon" style={{ width: '14px', height: '14px' }} />
          {gracePeriodError && "Grace Period must be at least 10 minutes."}
          {hourlyPriceError && <span>{gracePeriodError && <br />}Hourly Price cannot exceed $100.</span>}
          {dailyMaxPriceError && <span>{(gracePeriodError || hourlyPriceError) && <br />}Daily Maximum Price cannot exceed $100.</span>}
          {dailyMaxPriceEmptyError && <span>{(gracePeriodError || hourlyPriceError || dailyMaxPriceError) && <br />}Please set a daily maximum amount.</span>}
          {maxTimeMinError && <span>{(gracePeriodError || hourlyPriceError || dailyMaxPriceError || dailyMaxPriceEmptyError) && <br />}Maximum Hours must be 1 hour or greater.</span>}
          {ticketAmountError && <span>{(gracePeriodError || hourlyPriceError || dailyMaxPriceError || dailyMaxPriceEmptyError || maxTimeMinError) && <br />}Ticket Amount cannot exceed $250.</span>}
        </p>
      )}

      {/* QR Code Modal */}
      {lotData && (
        <QRCodeModal
          isOpen={isQrModalOpen}
          onClose={() => setIsQrModalOpen(false)}
          lotId={lotData.lotId}
          displayLotId={lotData.displayLotId || lotDisplayId}
        />
      )}

      {/* Modals */}
      {modalType === "confirmSave" && (
        <Modal
          isOpen={true}
          title="Confirm Changes"
          description="By continuing, you acknowledge that you are not misinforming your customers of pricing and that this change is being recorded in case of disputes. Current parked vehicles will not be affected by these changes."
          confirmText={isSaving ? "Saving..." : "Update Settings"}
          cancelText="Return"
          onConfirm={() => {
            handleSaveSettings();
            setModalType(null);
            setIsAcknowledged(false);
          }}
          onCancel={() => {
            setModalType(null);
            setIsAcknowledged(false);
          }}
          disableConfirm={!isAcknowledged || isSaving}
        >
          <div className="settings-acknowledge">
            <input
              type="checkbox"
              id="acknowledge-checkbox"
              checked={isAcknowledged}
              onChange={(e) => setIsAcknowledged(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="acknowledge-checkbox" style={{ cursor: 'pointer' }}>
              I acknowledge
            </label>
          </div>
        </Modal>
      )}

      {modalType === "unsavedChanges" && (
        <Modal
          isOpen={true}
          title="You have unsaved changes!"
          description="The changes you made will not be applied unless you save before switching to another page."
          confirmText="Take me back!"
          cancelText="Continue Anyway"
          onConfirm={() => setModalType(null)}
          onCancel={() => {
            if (pendingAction) {
              pendingAction(); //  Execute stored action
              setPendingAction(null); //  Clear the stored action
            }
            setModalType(null);
          }}
          swapKeyboardActions={true}
        />
      )}

      {modalType === "demoLotAddressBlocked" && (
        <Modal
          isOpen={true}
          title="Cannot change the address of a demo lot"
          description="Demo lots have a fixed address. Disable demo mode (or use superadmin mode) to edit the address."
          confirmText=""
          cancelText="OK"
          onConfirm={() => { }}
          onCancel={() => setModalType(null)}
        />
      )}

      {modalType === "confirmFreeParking" && (
        <Modal
          isOpen={true}
          title="Free Parking is Enabled"
          description="You are in free parking mode. Parked cars will not be billed."
          confirmText="Confirm Settings"
          cancelText="Return"
          onConfirm={() => {
            handleSaveSettings();
            setModalType(null);
          }}
          onCancel={() => setModalType(null)}
        />
      )}

      {modalOpen && modalType === "tutorialVideo" && (
        <VideoModal
          isOpen
          title="Settings Tutorial"
          videoUrl="https://www.youtube.com/watch?v=yX2hsXyotp8"
          onClose={() => {
            setModalType(null);
            setModalOpen(false);
          }}
        />
      )}

      {pendingAdvancedToggle && (
        <Modal
          isOpen
          title={pendingAdvancedToggle.field === "allowBetaAi" ? "Enable Allow Beta AI?" : "Enable Demo Lot?"}
          description={
            pendingAdvancedToggle.field === "allowBetaAi"
              ? "Parallel AI is billed to Parallel at roughly $0.05 per day once enabled. Confirm this lot should have access?"
              : "Demo mode exposes demo tooling like Generate Data inside Operator. Only enable this for sandbox lots."
          }
          confirmText="Enable"
          cancelText="Cancel"
          onConfirm={() => {
            const toggle = pendingAdvancedToggle;
            setPendingAdvancedToggle(null);
            updateAdvancedFlag(toggle.field, toggle.value);
          }}
          onCancel={() => setPendingAdvancedToggle(null)}
        />
      )}

      {deleteModalOpen && (
        <Modal
          isOpen
          title="Delete Lot"
          description={
            <div className="lot-delete-description">
              <p>This will delete the archived lot from Operator. It will be kept only as an audit record.</p>
              {(lotData?.currentOccupancy ?? 0) > 0 && (
                <p>Clear active sessions before deleting this lot.</p>
              )}
            </div>
          }
          confirmText={isDeletingLot ? "Deleting..." : "Delete Lot"}
          cancelText="Cancel"
          onConfirm={handleDeleteLot}
          onCancel={() => {
            if (!isDeletingLot) {
              setDeleteModalOpen(false);
            }
          }}
          disableConfirm={isDeletingLot || lotStatus !== "Suspended" || (lotData?.currentOccupancy ?? 0) > 0}
        />
      )}

      {/* Lot Settings Edit Popup (Fixed Input Field) */}
      {editingField && (
        <Modal
          isOpen={true}
          title={`Edit ${fieldLabels[editingField] || editingField}`}
          description="Please confirm you would like to update this setting. These settings may be used for billing and analytics, modifications should be reviewed thoroughly."
          confirmText="Update Settings"
          cancelText="Cancel"
          onConfirm={() => {
            saveEditPopup();
            setEditingField(null); // Ensure modal closes
          }}
          onCancel={() => {
            setEditingField(null);
            // Reset validation states
            setLotNameError(false);
            setCompanyNameError(false);
            setAddressError(false);
            setLotCapacityError(false);
          }}
          disableConfirm={
            (editingField === "lotName" && lotNameError) ||
            (editingField === "companyName" && companyNameError) ||
            (editingField === "address" && addressError) ||
            (editingField === "lotCapacity" && lotCapacityError)
          }
        >
          <input
            type={editingField === "lotCapacity" ? "number" : "text"}
            min="0"
            maxLength={fieldLimits[editingField] || undefined}
            value={tempValue}
            onChange={(e) => {
              let newValue = e.target.value;

              if (editingField === "lotCapacity") {
                // Remove any non-numeric characters
                newValue = newValue.replace(/[^0-9]/g, "");
                // Prevent leading zeros
                newValue = newValue.replace(/^0+/, '') || '0';
                // Validate capacity
                setLotCapacityError(!validateLotCapacity(newValue));
              } else if (editingField === "lotName") {
                setLotNameError(!validateLotName(newValue));
              } else if (editingField === "companyName") {
                setCompanyNameError(!validateCompanyName(newValue));
              } else if (editingField === "address") {
                setAddressError(!validateAddress(newValue));
              }

              setTempValue(newValue);
            }}
            onKeyDown={(e) => {
              if (editingField === "lotCapacity") {
                // Allow: backspace, delete, tab, escape, enter
                if ([8, 9, 13, 27, 46].indexOf(e.keyCode) !== -1 ||
                  // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                  (e.ctrlKey && [65, 67, 86, 88].indexOf(e.keyCode) !== -1) ||
                  // Allow: home, end, left, right
                  (e.keyCode >= 35 && e.keyCode <= 39) ||
                  // Allow numbers
                  (/[0-9]/.test(e.key))) {
                  return;
                }
                // Prevent the default action for all other keys
                e.preventDefault();
              }
            }}
          />
          {editingField === "lotName" && lotNameError && (
            <p className="error" style={{ color: 'var(--error)', marginTop: '8px', fontSize: '14px' }}>
              {tempValue.trim().length === 0 ? 'Lot name is required' : `Lot name must be ${fieldLimits.lotName} characters or less`}
            </p>
          )}
          {editingField === "companyName" && companyNameError && (
            <p className="error" style={{ color: 'var(--error)', marginTop: '8px', fontSize: '14px' }}>
              {tempValue.trim().length === 0 ? 'Company name is required' : `Company name must be ${fieldLimits.companyName} characters or less`}
            </p>
          )}
          {editingField === "address" && addressError && (
            <p className="error" style={{ color: 'var(--error)', marginTop: '8px', fontSize: '14px' }}>
              {tempValue.trim().length === 0 ? 'Address is required' : `Address must be ${fieldLimits.address} characters or less`}
            </p>
          )}
          {editingField === "lotCapacity" && lotCapacityError && (
            <p className="error" style={{ color: 'var(--error)', marginTop: '8px', fontSize: '14px' }}>
              {tempValue.trim().length === 0 ? 'Lot capacity is required' : 'Lot capacity must be a valid number with 5 digits or less'}
            </p>
          )}
        </Modal>
      )}

      {/* Address Modal */}
      {addressModalOpen && (
        <Modal
          isOpen={true}
          title="Edit Address"
          description="Please enter the complete address information. All fields are required."
          confirmText="Update Address"
          cancelText="Cancel"
          onConfirm={saveAddressChanges}
          onCancel={() => {
            setAddressModalOpen(false);
            setStreetNumberError(false);
            setStreetNameError(false);
            setCityError(false);
            setProvinceStateError(false);
            setPostalCodeError(false);
          }}
          disableConfirm={
            streetNumberError || streetNameError || cityError ||
            provinceStateError || postalCodeError ||
            !streetNumber.trim() || !streetName.trim() || !city.trim() ||
            !provinceState || !postalCode.trim() || isAddressLocked
          }
        >
          <div className="address-form">
            <input
              type="text"
              className={`address-input ${streetNumberError ? 'invalid' : ''}`}
              value={streetNumber}
              maxLength={10}
              onChange={(e) => {
                // Only allow numbers
                const value = e.target.value.replace(/[^0-9]/g, '');
                setStreetNumber(value);
                setStreetNumberError(!validateStreetNumber(value));
                setIsDirty(true);
              }}
              placeholder="Street Number (e.g., 123)"
              disabled={isAddressLocked}
            />

            <input
              type="text"
              className={`address-input ${streetNameError ? 'invalid' : ''}`}
              value={streetName}
              maxLength={50}
              onChange={(e) => {
                const value = e.target.value;
                setStreetName(value);
                setStreetNameError(!validateStreetName(value));
                setIsDirty(true);
              }}
              placeholder="Street Name (e.g., Main Street)"
              disabled={isAddressLocked}
            />

            <input
              type="text"
              className={`address-input ${cityError ? 'invalid' : ''}`}
              value={city}
              maxLength={30}
              onChange={(e) => {
                const value = e.target.value;
                setCity(value);
                setCityError(!validateCity(value));
                setIsDirty(true);
              }}
              placeholder="City"
              disabled={isAddressLocked}
            />

            <div className="menu-wrapper">
              <Menu
                trigger={
                  <button
                    type="button"
                    className="address-menu-trigger"
                    disabled={isAddressLocked}
                  >
                    <span className="address-menu-value">{country}</span>
                    <img
                      src="/assets/ArrowDropdown.svg"
                      alt=""
                      className={`address-menu-arrow ${isCountryMenuOpen ? "open" : ""}`}
                    />
                  </button>
                }
                items={countryMenuItems}
                placement="bottom-start"
                onOpenChange={setIsCountryMenuOpen}
                allowNoSelection={false}
              />
            </div>

            <div className="menu-wrapper">
              <Menu
                trigger={
                  <button
                    type="button"
                    className={`address-menu-trigger ${provinceStateError ? "invalid" : ""}`}
                    disabled={isAddressLocked}
                  >
                    <span className="address-menu-value">{selectedProvinceStateLabel}</span>
                    <img
                      src="/assets/ArrowDropdown.svg"
                      alt=""
                      className={`address-menu-arrow ${isProvinceMenuOpen ? "open" : ""}`}
                    />
                  </button>
                }
                items={provinceStateMenuItems}
                placement="bottom-start"
                onOpenChange={setIsProvinceMenuOpen}
                allowNoSelection={false}
              />
            </div>

            <input
              type="text"
              className={`address-input ${postalCodeError ? 'invalid' : ''}`}
              value={postalCode}
              maxLength={country === "Canada" ? 7 : 10}
              onChange={(e) => {
                let value = e.target.value;

                if (country === "Canada") {
                  // Format Canadian postal code (remove spaces/hyphens, then add space)
                  value = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                  if (value.length > 3) {
                    value = value.substring(0, 3) + ' ' + value.substring(3, 6);
                  }
                } else {
                  // US ZIP code - allow numbers and hyphens
                  value = value.replace(/[^0-9-]/g, '');
                }

                setPostalCode(value);
                setPostalCodeError(!validatePostalCode(value));
                setIsDirty(true);
              }}
              placeholder={country === "Canada" ? "A1A 1A1" : "12345 or 12345-6789"}
              disabled={isAddressLocked}
            />
          </div>

          {/* Show address locked error message */}
          {isAddressLocked && (
            <p className="error" style={{ color: 'var(--error)', marginTop: '16px', fontSize: '14px' }}>
              <img src="/assets/error.svg" alt="Error icon" style={{ width: '14px', height: '14px', marginRight: '5px' }} />
              You can change your address again in {remainingTime}.
            </p>
          )}

          {/* Show errors under the button */}
          {(streetNumberError || streetNameError || cityError || provinceStateError || postalCodeError) && (
            <div style={{ marginTop: '16px' }}>
              {streetNumberError && streetNumber.trim().length > 0 && (
                <p className="error">Street number must contain only numbers (max 10 characters)</p>
              )}
              {streetNameError && streetName.trim().length > 0 && (
                <p className="error">Street name must be 50 characters or less</p>
              )}
              {cityError && city.trim().length > 0 && (
                <p className="error">City must be 30 characters or less</p>
              )}
              {provinceStateError && (
                <p className="error">Please select a {country === "Canada" ? "province" : "state"}</p>
              )}
              {postalCodeError && postalCode.trim().length > 0 && (
                <p className="error">
                  {country === "Canada"
                    ? "Invalid Canadian postal code format (e.g., A1A 1A1)"
                    : "Invalid ZIP code format (e.g., 12345 or 12345-6789)"
                  }
                </p>
              )}
            </div>
          )}
        </Modal>
      )}

    </div>
  );
};

export default Settings;
